import { useCallback, useEffect, useState } from "react";
import { useDependency } from "../../../../contexts/DependencyProvider";
import {
  ListStatusRequest,
  ListStatusType,
} from "../../../../services/soap/project/requests/ListStatusRequest";
import {
  Flex,
  View,
  TextField,
  MenuTrigger,
  Menu,
  Item,
  ActionButton,
  Text,
} from "@adobe/react-spectrum";
import Edit from "@spectrum-icons/workflow/Edit";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import {
  AtollonIcon,
  PrintIcon,
  EditIcon,
  ToolIcon,
  TrashIcon,
  ProjectIconBlue,
} from "../../Icons/IconsLib";
import ButtonsComponent from "./ButtonsComponent";
import { GetContextPathRequest } from "../../../../services/soap/project/requests/GetContextPathRequest";
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from "react-i18next";
import { GetProjectRequest } from "../../../../services/soap/project/requests/GetProjectRequest";
import { UpdateProjectRequest } from "../../../../services/soap/project/requests/UpdateProjectRequest";
import { CheckWorkFlowQueueRequest } from "../../../../services/soap/features/requests/CheckWorkFlowQueueRequest";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { AppDispatch } from "../../../../state/store";
import { useDispatch } from "react-redux";
import { setSelectedItem } from "../../../../state/Finder/finderSlice";
import { invokeFinderApp } from "../../../../utils/NavigationUtils";
import styles from "./HeaderToolbarComponent.module.css";
import { getDisplayContextPathNodes } from "./contextPathUtils";
interface IProjectComponentProps {
  headerData: any;
  statusColor: (color: string) => string;
  onPrint?: (item: FPAData) => void;
  onEdit?: (item: FPAData) => void;
  onDelete?: (item: FPAData) => void;
  onTool?: (item: FPAData) => void;
  onAI?: (item: FPAData) => void;
  onCopy?: (item: FPAData) => void;
  onMove?: (item: FPAData) => void;
  selectedItem?: FPAData;
}

const ProjectComponent = ({
  headerData,
  statusColor,
  onPrint,
  onEdit,
  onDelete,
  onTool,
  onAI,
  onCopy,
  onMove,
  selectedItem,
}: IProjectComponentProps) => {
  const { store, projectService, featureService } = useDependency();
  const [projectStatusList, setProjectStatusList] = useState<any>([]);
  const [fullPath, setFullPath] = useState<string>("");
  const [projectTypeName, setProjectTypeName] = useState<string>("");
  const [projectData, setProjectData] = useState<any>([]);
  let [activityStatus, setActivityStatus] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRefId, setIsEditingRefId] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedRefId, setEditedRefId] = useState("");

  // Local state to track updated values
  const [displayName, setDisplayName] = useState<string>("");
  const [displayRefId, setDisplayRefId] = useState<string>("");

  // State to control menu open/close for double-click support
  const [isNameMenuOpen, setIsNameMenuOpen] = useState(false);
  const [isRefIdMenuOpen, setIsRefIdMenuOpen] = useState(false);

  // Update display values when headerData changes
  useEffect(() => {
    setDisplayName(headerData?.name || "");
    setDisplayRefId(headerData?.refId || "");
  }, [headerData?.name, headerData?.refId]);

  useEffect(() => {
    (async () => {
      headerData && (await getFullPath());
      await getProjectData();
      await getProjectStatusArray();
      setActivityStatus(headerData?.customState);
    })();
  }, [headerData?.id]);

  const getFullPath = async () => {
    const response: any = await projectService.getContextPath(
      new GetContextPathRequest(
        store.Server,
        store.SessionId,
        headerData?.id || 0,
      ),
    );
    //console.log('response.LIST[0].typeName', response.LIST[0].CONTEXT.typeName);
    setProjectTypeName(response.LIST[0].CONTEXT?.typeName);
    const modifiedList = getDisplayContextPathNodes(
      response.LIST,
      headerData?.id,
      false,
    );
    //console.log('getFullPath response', response);
    const pathString = modifiedList
      .map((item: any) => item.name)
      .join(" > ");
    setFullPath(pathString);
  };

  const getProjectStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listProjectStatus2(
      new ListStatusRequest(
        store.Server,
        store.SessionId,
        ListStatusType.Project,
      ),
    );

    if (res.result === "OK" && res.STATUS.length > 0) {
      res?.STATUS.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color, TYPE } = status;
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color, types: TYPE });
      });

      var filtered_status = statusArray.filter((status: any) =>
        status.types.find((type: any) => type.id === headerData?.projectType),
      );

      setProjectStatusList(filtered_status);
    }
  }, []);

  function itemTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5}>{data.name}</View>
      </Flex>
    );
  }

  function valueTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5} UNSAFE_style={{ marginLeft: "10px" }}>
          <span title={data?.name}>
            {data?.name && data?.name.length > 16
              ? data?.name.substring(0, 16) + "..."
              : data?.name}
          </span>
        </View>
      </Flex>
    );
  }

  async function getProjectData() {
    if (selectedItem) {
      let { PROJECT } = await projectService.getProject(
        new GetProjectRequest(store.Server, store.SessionId, selectedItem.id),
      );
      setProjectData(PROJECT);
    }
  }
  async function updateProjectStatus(e: any) {
    let resProject = await projectService.updateProject(
      new UpdateProjectRequest(store.Server, store.SessionId, {
        ...projectData,
        comment: selectedItem?.comment ?? projectData?.comment,
        customState: e.value,
      }),
    );
    let status = projectStatusList.find((status: any) => status.id === e.value);
    if (resProject.result === "OK") {
      await featureService.checkWorkFlowQueue(
        new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
      );
      ToastQueue.positive(
        t("status_updated_successfully", { ns: "layout_components" }),
        { timeout: 1000 },
      );
      dispatch(
        setSelectedItem({
          item: selectedItem
            ? {
                ...selectedItem,
                statusName: status.name,
                statusColor: statusColor(status.color),
              }
            : null,
          show_context: false,
        }),
      );
    } else {
      ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  const handleMasterProjectInfoClick = (id: string) => {
    if (id) {
      invokeFinderApp(id, FPADataTypes.PROJECT);
    }
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/finder?id=${headerData?.id}`;
    navigator.clipboard.writeText(url);
    ToastQueue.positive(
      t("link_copied_to_clipboard", { ns: "layout_components" }),
      { timeout: 1000 },
    );
  };

  // Handler to start editing project name
  const handleEditName = () => {
    setEditedName(displayName || "");
    setIsEditingName(true);
    setIsNameMenuOpen(false);
  };

  // Handler to start editing refId
  const handleEditRefId = () => {
    setEditedRefId(displayRefId || "");
    setIsEditingRefId(true);
    setIsRefIdMenuOpen(false);
  };

  // Handler to save project name
  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === displayName) {
      setIsEditingName(false);
      return;
    }

    try {
      let resProject = await projectService.updateProject(
        new UpdateProjectRequest(store.Server, store.SessionId, {
          ...projectData,
          name: editedName,
        }),
      );

      if (resProject.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("project_name_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Project name updated successfully",
          }),
          { timeout: 1000 },
        );
        // Update local display state
        setDisplayName(editedName);
        dispatch(
          setSelectedItem({
            item: selectedItem
              ? {
                  ...selectedItem,
                  title: editedName,
                }
              : null,
            show_context: false,
          }),
        );
        // Refresh project data
        await getProjectData();
      } else {
        ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
        ToastQueue.negative(
          error?.message ||
            t("failed_to_update_project", {
              ns: "layout_components",
              defaultValue: "Failed to update project",
            }),
          { timeout: 1000 },
        );
    }
    setIsEditingName(false);
  };

  // Handler to save refId
  const handleSaveRefId = async () => {
    if (editedRefId === displayRefId) {
      setIsEditingRefId(false);
      return;
    }

    try {
      let resProject = await projectService.updateProject(
        new UpdateProjectRequest(store.Server, store.SessionId, {
          ...projectData,
          refId: editedRefId,
        }),
      );

      if (resProject.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("refid_updated_successfully", {
            ns: "layout_components",
            defaultValue: "RefId updated successfully",
          }),
          { timeout: 1000 },
        );
        // Update local display state
        setDisplayRefId(editedRefId);
        // Refresh project data
        await getProjectData();
      } else {
        ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
        ToastQueue.negative(
          error?.message ||
            t("failed_to_update_project", {
              ns: "layout_components",
              defaultValue: "Failed to update project",
            }),
          { timeout: 1000 },
        );
    }
    setIsEditingRefId(false);
  };

  // Handler to cancel editing
  const handleCancelEdit = (type: "name" | "refId") => {
    if (type === "name") {
      setIsEditingName(false);
      setEditedName("");
    } else {
      setIsEditingRefId(false);
      setEditedRefId("");
    }
  };

  return (
    <Flex width="100%">
      <View UNSAFE_className={styles.header_main}>
        <View UNSAFE_className={styles.header_top_row}>
          <View UNSAFE_className={styles.header_left_column}>
            <View UNSAFE_className={styles.header_child}>
              <View UNSAFE_className={styles.frame_parent}>
                <View UNSAFE_className={styles.icon_activity_parent}>
                  <ProjectIconBlue size={32} />
                  {isEditingName ? (
                    <TextField
                      value={editedName}
                      onChange={setEditedName}
                      onBlur={handleSaveName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveName();
                        if (e.key === "Escape") handleCancelEdit("name");
                      }}
                      width="300px"
                      autoFocus
                      isQuiet
                    />
                  ) : (
                    <MenuTrigger
                      trigger="longPress"
                      isOpen={isNameMenuOpen}
                      onOpenChange={setIsNameMenuOpen}
                    >
                      <div onDoubleClick={() => setIsNameMenuOpen(true)}>
                        <ActionButton
                          isQuiet
                          UNSAFE_style={{
                            padding: 0,
                            minWidth: "auto",
                            background: "transparent",
                            border: "none",
                            cursor: "text",
                            height: "auto",
                            fontWeight: "bold",
                          }}
                          UNSAFE_className={styles.activity_name}
                        >
                          <span
                            style={{ fontSize: "20px", padding: "5px" }}
                            title={displayName}
                          >
                            {displayName?.length > 55
                              ? displayName.substring(0, 55) + "..."
                              : displayName}
                          </span>
                        </ActionButton>
                      </div>
                      <Menu onAction={(key) => key === "edit" && handleEditName()}>
                        <Item key="edit">
                          <Edit size="S" />
                          <Text>
                            {t("edit_name", {
                              ns: "layout_components",
                              defaultValue: "Edit Name",
                            })}
                          </Text>
                        </Item>
                      </Menu>
                    </MenuTrigger>
                  )}
                </View>

                <View UNSAFE_className={styles.folder_name_parent}>
                  <View UNSAFE_className={styles.folder_name}>
                    {fullPath.split(" > ").map((part, index, array) => (
                      <span key={index}>
                        {part}
                        {index < array.length - 1 && (
                          <span style={{ color: "#9CA3AF", margin: "0 4px" }}>
                            {" "}
                            &gt;{" "}
                          </span>
                        )}
                      </span>
                    ))}
                  </View>
                </View>
              </View>
              <View UNSAFE_className={styles.tag_parent} marginTop={20}>
                <View UNSAFE_className={styles.tag}>
                  <View UNSAFE_className={styles.activity_type}>
                    <View UNSAFE_className={styles.activity_type_text}>
                      {projectTypeName}
                    </View>

                    {isEditingRefId ? (
                      <>
                        <View UNSAFE_className={styles.divider}></View>
                        <TextField
                          value={editedRefId}
                          onChange={setEditedRefId}
                          onBlur={handleSaveRefId}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveRefId();
                            if (e.key === "Escape") handleCancelEdit("refId");
                          }}
                          width="150px"
                          autoFocus
                          isQuiet
                        />
                      </>
                    ) : displayRefId ? (
                      <>
                        <View
                          UNSAFE_className={styles.divider}
                          UNSAFE_style={{ backgroundColor: "#fff" }}
                        ></View>
                        <MenuTrigger
                          trigger="longPress"
                          isOpen={isRefIdMenuOpen}
                          onOpenChange={setIsRefIdMenuOpen}
                        >
                          <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                            <ActionButton
                              isQuiet
                              UNSAFE_style={{
                                padding: 0,
                                minWidth: "auto",
                                background: "transparent",
                                border: "none",
                                cursor: "text",
                                height: "auto",
                              }}
                            >
                              <span
                                style={{ fontSize: "12px", padding: "5px" }}
                                className={styles.ref_id}
                              >
                                {displayRefId}
                              </span>
                            </ActionButton>
                          </div>
                          <Menu
                            onAction={(key) => key === "edit" && handleEditRefId()}
                          >
                            <Item key="edit">
                              <Edit size="S" />
                              <Text>
                                {t("edit_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Edit RefID",
                                })}
                              </Text>
                            </Item>
                          </Menu>
                        </MenuTrigger>
                      </>
                    ) : (
                      <>
                        <View
                          UNSAFE_className={styles.divider}
                          UNSAFE_style={{ backgroundColor: "#fff" }}
                        ></View>
                        <MenuTrigger
                          trigger="longPress"
                          isOpen={isRefIdMenuOpen}
                          onOpenChange={setIsRefIdMenuOpen}
                        >
                          <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                            <ActionButton
                              isQuiet
                              UNSAFE_style={{
                                padding: 0,
                                minWidth: "auto",
                                background: "transparent",
                                border: "none",
                                cursor: "text",
                                height: "auto",
                              }}
                            >
                              <span
                                style={{ fontSize: "12px", padding: "5px" }}
                                className={styles.ref_id}
                              >
                                {t("add_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Add RefID",
                                })}
                              </span>
                            </ActionButton>
                          </div>
                          <Menu
                            onAction={(key) => key === "edit" && handleEditRefId()}
                          >
                            <Item key="edit">
                              <Edit size="S" />
                              <Text>
                                {t("add_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Add RefID",
                                })}
                              </Text>
                            </Item>
                          </Menu>
                        </MenuTrigger>
                      </>
                    )}
                  </View>
                </View>
              </View>
              {headerData?.masterFolderName && headerData?.masterProjectName && (
                <View UNSAFE_className={styles.master_project_info}>
                  <span
                    onClick={() =>
                      handleMasterProjectInfoClick(headerData?.masterFolderId)
                    }
                  >
                    {headerData?.masterFolderName}
                  </span>
                  <span style={{ color: "#9CA3AF", margin: "0 4px" }}> &gt; </span>
                  <span
                    onClick={() =>
                      handleMasterProjectInfoClick(headerData?.masterProjectId)
                    }
                  >
                    {headerData?.masterProjectName}
                  </span>
                </View>
              )}
            </View>
          </View>
          <View UNSAFE_className={styles.header_right_column}>
            <View UNSAFE_className={styles.header_actions_row}>
              <View UNSAFE_className={styles.status_picker_wrap}>
                {projectStatusList.length > 0 && (
                  <DropDownListComponent
                    cssClass={styles.status_dropdown}
                    width={"100%"}
                    dataSource={projectStatusList}
                    fields={{ text: "name", value: "id" }}
                    showClearButton={true}
                    itemTemplate={itemTemplate}
                    valueTemplate={valueTemplate}
                    value={activityStatus}
                    onChange={(e: any) => {
                      updateProjectStatus(e);
                    }}
                  />
                )}
              </View>
              <View UNSAFE_className={styles.actions_wrap}>
                <ButtonsComponent
                  styles={styles}
                  AtollonIcon={AtollonIcon}
                  PrintIcon={PrintIcon}
                  EditIcon={EditIcon}
                  ToolIcon={ToolIcon}
                  TrashIcon={TrashIcon}
                  onPrint={onPrint}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onTool={onTool}
                  onAI={onAI}
                  selectedItem={selectedItem}
                  onCopy={onCopy}
                  onMove={onMove}
                  onCopyLink={copyUrl}
                />
              </View>
            </View>
          </View>
        </View>
      </View>
    </Flex>
  );
};

export default ProjectComponent;
